import { makeAutoObservable, runInAction } from 'mobx'

import { LoadDiskFolderItemsTask } from '../Tasks/LoadDiskFolderItemsTask'

import type { DiskNode } from '../Contracts/DiskNode'

/**
 * Дерево папок, що вантажиться гілками: дочірні папки читаються, коли гілку
 * розгортають (ліва панель, модалка «Перемістити/Копіювати»). Кеш спільний —
 * розгорнуте в панелі вже не тягнеться вдруге в модалці.
 *
 * Після змін у дереві (нова папка, переміщення, кошик) екран скидає кеш
 * змінених гілок через `invalidate`.
 */
export class DiskFolderTreeStore {
    /** Дочірні папки за uuid батьківської папки. */
    children = new Map<string, DiskNode[]>()
    loading = new Set<string>()
    /** Розгорнуті гілки лівої панелі. */
    expanded = new Set<string>()

    constructor() {
        makeAutoObservable(this)
    }

    childrenOf(parent: string): DiskNode[] | undefined {
        return this.children.get(parent)
    }

    isLoaded(parent: string): boolean {
        return this.children.has(parent)
    }

    async load(parent: string, force = false): Promise<DiskNode[]> {
        if (!force && this.children.has(parent)) return this.children.get(parent) ?? []
        if (this.loading.has(parent)) return this.children.get(parent) ?? []

        this.loading.add(parent)

        try {
            const { items } = await LoadDiskFolderItemsTask({ parent, foldersOnly: true })

            runInAction(() => this.children.set(parent, items))

            return items
        } catch {
            // Гілка, що не відкрилася, лишається згорнутою — дерево не падає
            // через одну папку; сам вміст екран показує з власною помилкою
            runInAction(() => this.children.set(parent, []))

            return []
        } finally {
            runInAction(() => this.loading.delete(parent))
        }
    }

    isExpanded(uuid: string): boolean {
        return this.expanded.has(uuid)
    }

    toggle(uuid: string): void {
        if (this.expanded.has(uuid)) {
            this.expanded.delete(uuid)
        } else {
            this.expanded.add(uuid)
            void this.load(uuid)
        }
    }

    /** Розгорнути шлях до відкритої папки — щоб вона була видна в панелі. */
    expand(uuids: string[]): void {
        for (const uuid of uuids) {
            this.expanded.add(uuid)
            void this.load(uuid)
        }
    }

    /** Скинути кеш гілок; розгорнуті гілки перечитуються. */
    invalidate(parents: Array<string | null | undefined>): void {
        for (const parent of parents) {
            if (parent && this.children.has(parent)) void this.load(parent, true)
        }
    }

    reset(): void {
        this.children.clear()
    }
}
